import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck } from "@phosphor-icons/react";
import { images } from "@/data/site";

const titleLines = ["Engineering", "Excellence,", "Equipment", "Reliability"];
const highlights = [
  ["01", "Manpower Supply"],
  ["02", "Equipment Rental"],
  ["03", "Transaction Support"],
];

const fadeLeft = { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 } };
const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const ctaFade = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };
const lineEase = [0.22, 1, 0.36, 1];

const HeroHeadline = () => (
  <h1 className="font-heading text-4xl font-bold uppercase leading-[0.88] tracking-tighter text-industrial-text sm:text-5xl lg:text-6xl" data-testid="hero-headline">
    {titleLines.map((line, index) => (
      <span key={line} className="block overflow-hidden pb-1">
        <motion.span
          className={index === 3 ? "block text-industrial-yellow" : "block"}
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 + index * 0.12, ease: lineEase }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </h1>
);

const HeroActions = () => (
  <motion.div {...ctaFade} transition={{ delay: 1.05 }} className="mt-10 flex flex-col gap-4 sm:flex-row">
    <a href="#contact" data-testid="hero-get-quote-button" className="group inline-flex items-center justify-center gap-3 bg-industrial-yellow px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-industrial-black transition-all hover:-translate-y-1 hover:hard-shadow-sm">
      Get a Quote <span aria-hidden="true" className="text-base leading-none">↗</span>
    </a>
    <a href="#equipment" data-testid="hero-hire-equipment-button" className="inline-flex items-center justify-center gap-3 border border-industrial-line px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-industrial-text transition-colors hover:border-industrial-yellow hover:text-industrial-yellow">
      Hire Equipment <span aria-hidden="true" className="text-base leading-none">↓</span>
    </a>
  </motion.div>
);

const HeroHighlights = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-14 grid border border-industrial-line sm:grid-cols-3" data-testid="hero-capability-strip">
    {highlights.map(([value, label]) => (
      <div key={label} className="flex items-center gap-4 border-b border-industrial-line p-4 last:border-b-0 sm:block sm:border-b-0 sm:border-r sm:last:border-r-0">
        <p className="font-heading text-2xl font-bold text-industrial-yellow">{value}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-industrial-text sm:mt-1">{label}</p>
      </div>
    ))}
  </motion.div>
);

const HeroVisual = ({ imageY, rotate }) => (
  <div className="relative min-h-[480px] lg:col-span-5">
    <motion.div style={{ y: imageY, rotate }} className="spotlight-image absolute inset-6 overflow-hidden border border-industrial-line bg-industrial-surface" data-testid="hero-industrial-image-frame">
      <motion.img initial={{ scale: 1.14 }} animate={{ scale: 1 }} transition={{ duration: 1.5, ease: lineEase }} src={images.hero} alt="Industrial cranes at a construction site" className="h-full w-full object-cover grayscale-[30%] contrast-125" />
      <div className="absolute inset-x-0 top-0 h-1 overflow-hidden bg-white/10"><div className="h-full w-1/2 animate-scan bg-industrial-yellow" /></div>
    </motion.div>
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.25 }} className="absolute bottom-12 left-0 max-w-xs border border-industrial-yellow bg-white p-6 hard-shadow" data-testid="hero-safety-card">
      <ShieldCheck size={34} weight="fill" className="text-industrial-yellow" />
      <p className="mt-4 font-heading text-2xl font-semibold uppercase text-industrial-text">Safety-led mobilization</p>
      <p className="mt-2 text-sm leading-6 text-industrial-muted">Certified manpower, maintained machines, and execution discipline for demanding sites.</p>
    </motion.div>
  </div>
);

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 2]);

  return (
    <section id="home" ref={ref} className="blueprint-grid relative min-h-screen overflow-hidden pt-20" data-testid="hero-section">
      <div className="absolute left-0 top-20 h-full w-px bg-industrial-line" />
      <div className="absolute right-0 top-20 h-full w-px bg-industrial-line" />
      <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-[1500px] lg:grid-cols-12">
        <div className="relative z-10 flex flex-col justify-center border-r border-industrial-line px-5 py-16 sm:px-8 lg:col-span-7 lg:py-24">
          <motion.p {...fadeLeft} transition={{ delay: 0.2 }} className="mb-8 flex items-center gap-3 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-industrial-yellow" data-testid="hero-eyebrow">
            <span className="h-px w-12 bg-industrial-yellow" /> PAN India Industrial Services
          </motion.p>
          <HeroHeadline />
          <motion.p {...fadeUp} transition={{ delay: 0.9 }} className="mt-8 max-w-2xl text-base leading-8 text-industrial-muted sm:text-lg" data-testid="hero-description">
            PAN India manpower supply, plant &amp; machinery rental, and transaction support for industrial, refinery, EPC, infrastructure, and construction projects.
          </motion.p>
          <HeroActions />
          <HeroHighlights />
        </div>
        <HeroVisual imageY={imageY} rotate={rotate} />
      </div>
    </section>
  );
}
